/**
 * The slider behind every carousel in the theme.
 *
 * One bundle, one Swiper: a page with two sliders loads the library once. Blocks
 * only print the markup and their options —
 *
 *   <div class="swiper" data-slider data-options='{"navigation":true}'>
 *     <div class="swiper-wrapper">…</div>
 *     <div class="swiper-pagination" data-slider-pagination></div>
 *   </div>
 *   <button data-slider-prev>…</button>
 *   <button data-slider-next>…</button>
 *
 * inc/blocks.php loads this only on pages that carry a block which needs it.
 */

import Swiper from 'swiper';
import { A11y, Autoplay, Keyboard, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
// After the library, so the theme's own rules win.
import '../scss/slider.scss';

type SliderOptions = {
  navigation?: boolean;
  pagination?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  /** Slides per view at the widest breakpoint; smaller ones scale down from it. */
  perView?: number;
};

const readOptions = (element: HTMLElement): SliderOptions => {
  try {
    return JSON.parse(element.dataset.options || '{}') as SliderOptions;
  } catch {
    return {};
  }
};

const init = (): void => {
  document.querySelectorAll<HTMLElement>('[data-slider]').forEach((element) => {
    const options = readOptions(element);
    const scope = element.closest<HTMLElement>('.swiper-scope') || element.parentElement;
    const perView = Math.max(1, Math.min(4, options.perView || 3));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Looping needs more slides than fit on screen — Swiper has to have
    // something to clone. With too few it would jump instead of flowing, so the
    // option quietly stands down.
    const slides = element.querySelectorAll('.swiper-slide').length;
    const loop = !!options.loop && slides > perView;

    new Swiper(element, {
      modules: [A11y, Autoplay, Keyboard, Navigation, Pagination],
      // Just over one slide on a phone, so the next card peeks in and the strip
      // reads as scrollable without an instruction.
      slidesPerView: 1.1,
      spaceBetween: 20,
      watchOverflow: true,
      loop,
      // Without this a looped slider can show a gap while it rearranges clones.
      loopAdditionalSlides: loop ? perView : 0,
      keyboard: { enabled: true },
      a11y: { enabled: true },
      breakpoints: {
        768: { slidesPerView: Math.min(2, perView), spaceBetween: 24 },
        1024: { slidesPerView: perView, spaceBetween: 32 },
      },
      navigation: options.navigation
        ? {
            prevEl: scope?.querySelector('[data-slider-prev]') as HTMLElement,
            nextEl: scope?.querySelector('[data-slider-next]') as HTMLElement,
          }
        : false,
      pagination: options.pagination
        ? { el: element.querySelector('[data-slider-pagination]') as HTMLElement, clickable: true }
        : false,
      // Autoplay is opt-in per block, and never runs for reduced-motion visitors.
      autoplay: options.autoplay && !reduced ? { delay: 5000, disableOnInteraction: true } : false,
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
